import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Upload, CheckCircle, Loader2, ShieldCheck, MapPin, FileText, Lock, Unlock, Building2, RotateCcw } from "lucide-react";
import { SubmissionStatusTimeline } from "@/components/SubmissionStatusTimeline";

const ACCEPTED_DOC_TYPES = ".jpg,.jpeg,.pdf";
const MAX_DOC_SIZE = 5 * 1024 * 1024;
const STATUS_STYLES = {
  pending: { bg: "bg-amber-100 text-amber-800", icon: ShieldCheck },
  under_review: { bg: "bg-blue-100 text-blue-800", icon: ShieldCheck },
  verified: { bg: "bg-green-100 text-green-800", icon: CheckCircle },
  rejected: { bg: "bg-red-100 text-red-800", icon: ShieldCheck },
  retracted: { bg: "bg-slate-100 text-slate-800", icon: RotateCcw },
};
const LEVEL_NAMES = { 0: "Unverified", 1: "Level 1 — Business Verified", 2: "Level 2 — Fully Verified" };

export default function ProviderVerification() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [status, setStatus] = useState(null);
  const [documents, setDocuments] = useState({ documents: [], checklist: [] });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [retracting, setRetracting] = useState(false);
  const [form, setForm] = useState({
    legal_entity_name: "", business_registration_number: "", business_type: "DTI",
    registered_business_address: "", venue_physical_address: "",
    gps_latitude: null, gps_longitude: null, business_permit_number: "",
    authorized_signatory_name: "", bank_account_name: "", bank_account_number: "",
    bank_name: "", ownership_declaration: false,
  });
  const [formInitialized, setFormInitialized] = useState(false);

  const normalizeBusinessType = (value) => {
    if (value === "Government") return "Government/LGU";
    if (value === "Community") return "Community/Home Owners Association";
    return value || "DTI";
  };

  const fetchData = useCallback(async () => {
    try {
      const [vRes, sRes, dRes] = await Promise.all([
        api.get(`/venues/${venueId}`),
        api.get(`/vov/status/${venueId}`),
        api.get(`/vov/documents/${venueId}`),
      ]);
      setVenue(vRes.data);
      setStatus(sRes.data);
      setDocuments(dRes.data);
      // Only pre-fill form on initial load, not on subsequent refreshes
      if (!formInitialized) {
        const v = vRes.data;
        const vStatus = sRes.data?.ownership_verification_status;
        const isEditable = !["pending", "under_review", "verified"].includes(vStatus);
        setForm(prev => ({
          ...prev,
          legal_entity_name: v.legal_entity_name || "",
          business_registration_number: v.business_registration_number || "",
          business_type: normalizeBusinessType(v.business_type),
          registered_business_address: v.registered_business_address || "",
          venue_physical_address: v.venue_physical_address || v.address || "",
          gps_latitude: v.gps_latitude || null,
          gps_longitude: v.gps_longitude || null,
          business_permit_number: v.business_permit_number || "",
          authorized_signatory_name: v.authorized_signatory_name || "",
          bank_account_name: v.bank_account_name || "",
          bank_account_number: v.bank_account_number || "",
          bank_name: v.bank_name || "",
          // Uncheck declaration when retracted/rejected so the form is editable
          ownership_declaration: isEditable ? false : (v.ownership_declaration || false),
        }));
        setFormInitialized(true);
      }
    } catch { toast.error("Failed to load venue"); }
    setLoading(false);
  }, [venueId, formInitialized]);

  // Refresh only documents (used after upload/delete to avoid resetting form)
  const refreshDocs = async () => {
    try {
      const dRes = await api.get(`/vov/documents/${venueId}`);
      setDocuments(dRes.data);
    } catch { /* silent */ }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async () => {
    if (!form.ownership_declaration) { toast.error("Please confirm the ownership declaration"); return; }
    if (!form.legal_entity_name.trim()) { toast.error("Legal entity name is required"); return; }
    const requiresRegistration = ["DTI", "SEC"].includes(form.business_type);
    if (requiresRegistration && !form.business_registration_number.trim()) { toast.error("Business registration number is required"); return; }
    if (!form.authorized_signatory_name.trim()) { toast.error("Authorized signatory name is required"); return; }
    setSubmitting(true);
    try {
      const res = await api.post(`/vov/submit/${venueId}`, form);
      if (res.data.blocked) {
        toast.error("High duplicate risk detected. Your venue is under manual review.");
      } else {
        toast.success("Verification submitted! Our team will review within 3-5 days.");
      }
      setFormInitialized(false); // Allow re-read on next fetch
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Submission failed"); }
    setSubmitting(false);
  };

  const handleRetract = async () => {
    setRetracting(true);
    try {
      await api.post(`/vov/retract/${venueId}`);
      toast.success("Verification retracted. You may now edit and resubmit.");
      setFormInitialized(false); // Allow form editing again
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Retract failed"); }
    setRetracting(false);
  };

  const uploadDoc = async (docType) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ACCEPTED_DOC_TYPES;
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > MAX_DOC_SIZE) { toast.error("File too large (max 5MB)"); return; }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("venue_id", venueId);
      formData.append("doc_type", docType);
      try {
        await api.post("/uploads/document", formData, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${file.name} uploaded successfully`);
        refreshDocs(); // Only refresh docs, not the whole form
      } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    };
    input.click();
  };

  const deleteDoc = async (docType) => {
    try {
      await api.delete(`/vov/document/${venueId}/${docType}`);
      toast.success("Document removed. You can upload a new one now.");
      refreshDocs(); // Only refresh docs, not the whole form
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete document"); }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setForm(prev => ({ ...prev, gps_latitude: pos.coords.latitude, gps_longitude: pos.coords.longitude }));
          toast.success("GPS location captured");
        },
        () => toast.error("Could not get location. Please enter manually.")
      );
    }
  };

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!venue) return null;

  const verStatus = status?.ownership_verification_status || "pending";
  const statusInfo = STATUS_STYLES[verStatus] || STATUS_STYLES.pending;
  const StatusIcon = statusInfo.icon;
  const isVerified = verStatus === "verified";
  const isSubmitted = ["pending", "under_review"].includes(verStatus);
  const isRetracted = verStatus === "retracted";
  // Form is read-only when submitted (pending/under_review) OR when declaration checkbox is checked (locks inputs)
  const isFormLocked = isSubmitted || form.ownership_declaration;
  const canEdit = !isVerified && !isSubmitted;

  // 30-minute cooldown check (client-side hint; server enforces)
  let canRetract = false;
  let retractMinutesLeft = 0;
  if (isSubmitted && status?.verification_submitted_at) {
    const elapsed = (Date.now() - new Date(status.verification_submitted_at).getTime()) / 1000;
    canRetract = elapsed >= 1800;
    retractMinutesLeft = Math.max(0, Math.ceil((1800 - elapsed) / 60));
  }

  const venueChecklistItems = [];
  if (status?.verification_review_notes) venueChecklistItems.push(`Admin note: ${status.verification_review_notes}`);
  if (!form.authorized_signatory_name) venueChecklistItems.push("Add or correct the Authorized Signatory name");
  documents.checklist.filter(item => item.required && !item.uploaded).forEach(item => venueChecklistItems.push(`Upload required document: ${item.label}`));

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6" data-testid="provider-verification-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex-1">
          <h1 className="font-heading text-xl sm:text-2xl">Venue Verification</h1>
          <p className="text-sm text-muted-foreground">{venue.name}</p>
        </div>
        <Badge className={statusInfo.bg} data-testid="verification-status">
          <StatusIcon className="w-3 h-3 mr-1" />{verStatus === "retracted" ? "Retracted" : verStatus}
        </Badge>
      </div>

      {/* Status Banner */}
      {isVerified ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3" data-testid="verified-banner">
          <CheckCircle className="w-6 h-6 text-green-600 shrink-0" />
          <div>
            <p className="font-medium text-green-800">Venue Verified — Level {status?.verification_level}</p>
            <p className="text-sm text-green-700">{LEVEL_NAMES[status?.verification_level || 0]}. Payouts are {status?.payout_status}.</p>
          </div>
        </div>
      ) : isRetracted ? (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <p className="font-medium text-slate-800">Verification Retracted</p>
          <p className="text-sm text-slate-600">You retracted your submission. Edit your information and resubmit when ready.</p>
        </div>
      ) : verStatus === "rejected" ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="font-medium text-red-800">Verification Rejected</p>
          <p className="text-sm text-red-700">Please review the rejection reason, edit your profile information, replace the required documents, and resubmit.</p>
        </div>
      ) : isSubmitted ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="font-medium text-blue-800">{verStatus === "under_review" ? "Under Review" : "Submission Pending Review"}</p>
          <p className="text-sm text-blue-700">Our team is reviewing your submission. This usually takes 3-5 days. Your form is now read-only.</p>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="font-medium text-amber-800">Verification Required</p>
          <p className="text-sm text-amber-700">Complete the form below to verify your venue ownership.</p>
        </div>
      )}

      {(verStatus === "rejected" || !!status?.verification_review_notes) && venueChecklistItems.length > 0 && (
        <Card data-testid="venue-resubmission-checklist">
          <CardHeader><CardTitle className="font-heading text-base">Resubmission Checklist</CardTitle></CardHeader>
          <CardContent>
            <ul className="list-disc pl-5 text-sm space-y-2 text-red-700">
              {venueChecklistItems.map((item, idx) => <li key={idx}>{item}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}

      <SubmissionStatusTimeline
        currentStatus={verStatus === 'verified' ? 'approved' : verStatus === 'under_review' ? 'under_review' : verStatus === 'rejected' ? 'rejected' : verStatus === 'retracted' ? 'submitted' : status?.verification_review_notes ? 'more_info_requested' : 'submitted'}
        submittedAt={venue.created_at}
        reviewNotes={status?.verification_review_notes}
        testId="venue-verification-timeline"
      />

      {/* Risk Scores */}
      {(status?.duplicate_risk_score > 0 || status?.venue_risk_score > 0) && (
        <div className="flex gap-4">
          <Card className="flex-1"><CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Duplicate Risk</p>
            <p className={`text-xl font-heading font-bold ${status.duplicate_risk_score >= 50 ? "text-red-500" : "text-green-600"}`}>{status.duplicate_risk_score}</p>
          </CardContent></Card>
          <Card className="flex-1"><CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Venue Risk</p>
            <p className={`text-xl font-heading font-bold ${status.venue_risk_score > 50 ? "text-red-500" : "text-green-600"}`}>{status.venue_risk_score}</p>
          </CardContent></Card>
          <Card className="flex-1"><CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Payout</p>
            <p className="text-xl font-heading font-bold flex items-center justify-center gap-1">
              {status.payout_status === "unlocked" ? <Unlock className="w-4 h-4 text-green-600" /> : <Lock className="w-4 h-4 text-red-500" />}
              <span className={status.payout_status === "unlocked" ? "text-green-600" : "text-red-500"}>{status.payout_status}</span>
            </p>
          </CardContent></Card>
        </div>
      )}

      {/* Verification Form */}
      {!isVerified && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Business Information
              {isFormLocked && <Badge variant="outline" className="text-[9px] ml-2"><Lock className="w-3 h-3 mr-1" />Read-only</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Legal Entity Name *</Label>
                <Input value={form.legal_entity_name} onChange={e => setForm({...form, legal_entity_name: e.target.value})} placeholder="Company / Business Name" data-testid="input-legal-name" disabled={isFormLocked} />
              </div>
              <div>
                <Label className="text-xs">Business Type *</Label>
                <Select value={form.business_type} onValueChange={v => setForm({...form, business_type: v})} disabled={isFormLocked}>
                  <SelectTrigger data-testid="select-business-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DTI">Sole Proprietorship</SelectItem>
                    <SelectItem value="SEC">Corporation</SelectItem>
                    <SelectItem value="Government/LGU">Government/LGU</SelectItem>
                    <SelectItem value="Community/Home Owners Association">Community/Home Owners Association</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Business Registration # *</Label>
                <Input value={form.business_registration_number} onChange={e => setForm({...form, business_registration_number: e.target.value})} placeholder="SEC/DTI Registration Number" data-testid="input-reg-number" disabled={isFormLocked} />
              </div>
              <div>
                <Label className="text-xs">Business Permit #</Label>
                <Input value={form.business_permit_number} onChange={e => setForm({...form, business_permit_number: e.target.value})} placeholder="Mayor's Permit Number" disabled={isFormLocked} />
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-xs">Registered Business Address</Label>
                <Input value={form.registered_business_address} onChange={e => setForm({...form, registered_business_address: e.target.value})} placeholder="As shown on registration" disabled={isFormLocked} />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Venue Physical Address</Label>
                <Input value={form.venue_physical_address} onChange={e => setForm({...form, venue_physical_address: e.target.value})} placeholder="Actual venue location" disabled={isFormLocked} />
              </div>
              <div>
                <Label className="text-xs">GPS Latitude</Label>
                <Input type="number" step="any" value={form.gps_latitude || ""} onChange={e => setForm({...form, gps_latitude: parseFloat(e.target.value) || null})} placeholder="14.5547" disabled={isFormLocked} />
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-xs">GPS Longitude</Label>
                  <Input type="number" step="any" value={form.gps_longitude || ""} onChange={e => setForm({...form, gps_longitude: parseFloat(e.target.value) || null})} placeholder="121.0244" disabled={isFormLocked} />
                </div>
                <Button type="button" variant="outline" size="sm" onClick={getLocation} className="shrink-0 h-9" data-testid="get-gps-btn" disabled={isFormLocked}>
                  <MapPin className="w-3 h-3 mr-1" />GPS
                </Button>
              </div>
            </div>

            <Separator />

            <div>
              <Label className="text-xs">Authorized Signatory Name *</Label>
              <Input value={form.authorized_signatory_name} onChange={e => setForm({...form, authorized_signatory_name: e.target.value})} placeholder="Full name of signatory" data-testid="input-signatory" disabled={isFormLocked} />
            </div>

            <Separator />
            <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Bank Account (for payouts)</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Bank Name</Label>
                <Input value={form.bank_name} onChange={e => setForm({...form, bank_name: e.target.value})} placeholder="BDO, BPI, etc." disabled={isFormLocked} />
              </div>
              <div>
                <Label className="text-xs">Account Name</Label>
                <Input value={form.bank_account_name} onChange={e => setForm({...form, bank_account_name: e.target.value})} placeholder="Account holder name" disabled={isFormLocked} />
              </div>
              <div>
                <Label className="text-xs">Account Number</Label>
                <Input value={form.bank_account_number} onChange={e => setForm({...form, bank_account_number: e.target.value})} placeholder="Account number" disabled={isFormLocked} />
              </div>
            </div>

            <Separator />

            {/* Ownership Declaration */}
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg" data-testid="ownership-declaration">
              <Checkbox checked={form.ownership_declaration} onCheckedChange={v => setForm({...form, ownership_declaration: v})} id="declaration" disabled={isSubmitted} />
              <label htmlFor="declaration" className="text-sm leading-relaxed cursor-pointer">
                I hereby declare that I am the <strong>legal owner or authorized representative</strong> of this venue, and that all information provided is true and correct. I understand that providing false information may result in account suspension and legal action.
              </label>
            </div>

          </CardContent>
        </Card>
      )}

      {/* Document Checklist */}
      <Card>
        <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Required Documents</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-muted-foreground">Allowed files: JPEG or PDF only, maximum 5MB each.</p>
          {documents.checklist.map(d => {
            const uploadedDoc = documents.documents.find(doc => doc.doc_type === d.doc_type);
            return (
              <div key={d.doc_type} className="flex items-center justify-between py-2 border-b last:border-0" data-testid={`doc-${d.doc_type}`}>
                <div className="flex items-center gap-2 min-w-0">
                  {d.uploaded ? <CheckCircle className="w-4 h-4 text-green-500 shrink-0" /> : <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" />}
                  <div className="min-w-0">
                    <span className="text-sm">{d.label}</span>
                    {d.required && <Badge variant="outline" className="text-[9px] ml-1">Required</Badge>}
                    {uploadedDoc && <p className="text-[10px] text-muted-foreground truncate">{uploadedDoc.file_name || "Uploaded"}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {uploadedDoc?.file_url && (
                    <a href={uploadedDoc.file_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline" onClick={e => e.stopPropagation()}>View</a>
                  )}
                  {!isSubmitted && uploadedDoc && (
                    <Button size="sm" variant="outline" onClick={() => deleteDoc(d.doc_type)} className="text-red-600 border-red-200" data-testid={`delete-${d.doc_type}`}>
                      Delete
                    </Button>
                  )}
                  {!isSubmitted && (
                    <Button size="sm" variant="outline" onClick={() => uploadDoc(d.doc_type)} data-testid={`upload-${d.doc_type}`}>
                      <Upload className="w-3 h-3 mr-1" />{d.uploaded ? "Replace" : "Upload"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Submit or Retract Button */}
      {!isVerified && canEdit && (
        <Button onClick={handleSubmit} disabled={submitting || !form.ownership_declaration} className="w-full rounded-full h-12" size="lg" data-testid="submit-verification-btn">
          <ShieldCheck className="w-4 h-4 mr-2" />
          {submitting ? "Submitting..." : "Submit for Verification"}
        </Button>
      )}

      {isSubmitted && (
        <div className="space-y-2">
          <Button onClick={handleRetract} disabled={retracting || !canRetract} variant="outline" className="w-full rounded-full h-12 border-2" size="lg" data-testid="retract-verification-btn">
            <RotateCcw className="w-4 h-4 mr-2" />
            {retracting ? "Retracting..." : "Retract and Edit Verification"}
          </Button>
          {!canRetract && (
            <p className="text-xs text-center text-muted-foreground" data-testid="retract-cooldown">
              You can retract after 30 minutes. {retractMinutesLeft > 0 ? `${retractMinutesLeft} minute(s) remaining.` : ""}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
